/**
 * /campaigns/:id/followup — FollowupRule + Messages page (Story 7.8).
 *
 * Surface:
 *   - Rule config section (delay, max_followups, active_window, active toggle)
 *   - Message pool with add / edit / delete / reorder (up/down)
 *   - Empty-pool guard banner (AC4): blocks `active=true` when pool is empty
 *
 * Data flow:
 *   - `GET /admin/campaigns/:id/followup-rule` → rule or null
 *   - `GET /admin/campaigns/:id/followup-rule/messages` → pool
 *   - `PUT /admin/campaigns/:id/followup-rule` → upsert rule
 *   - `POST/PUT/DELETE /admin/campaigns/:id/followup-rule/messages[/:id]`
 *
 * If the rule does not exist yet the page renders with defaults and the
 * message pool shows the empty state. Adding a message (POST messages)
 * requires an existing rule, so we render a "Save rule first" hint when
 * rule is null.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import {
  FollowupRuleForm,
  DEFAULT_FOLLOWUP_RULE,
  type FollowupRuleFormValue,
} from '@/components/followup/FollowupRuleForm';
import { FollowupMessagesList } from '@/components/followup/FollowupMessagesList';
import type { ActiveHoursValue } from '@/components/campaigns/ActiveHoursInput';

interface FollowupRuleDto {
  id: string;
  campaignId: string;
  delayHours: number;
  maxFollowups: number;
  activeWindow: ActiveHoursValue;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FollowupMessageDto {
  id: string;
  ruleId: string;
  position: number;
  content: string;
  createdAt: string;
  updatedAt: string;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: { code?: string; message?: string; field?: string; detail?: string };
    };
    if (body?.error?.detail === 'EMPTY_POOL_ACTIVE_RULE') {
      return 'Não é possível ativar a regra com o pool vazio. Adicione mensagens primeiro.';
    }
    const code = body?.error?.code;
    if (code === 'VALIDATION_FAILED') {
      const field = body.error?.field ? ` (campo: ${body.error.field})` : '';
      return `${body.error?.message ?? 'Validação falhou.'}${field}`;
    }
    if (code === 'CAMPAIGN_NOT_FOUND') return 'Campanha não encontrada.';
    if (code === 'FOLLOWUP_RULE_NOT_FOUND') return 'Regra de followup não configurada.';
    if (code === 'FOLLOWUP_MESSAGE_NOT_FOUND') return 'Mensagem não encontrada.';
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function toFormValue(r: FollowupRuleDto): FollowupRuleFormValue {
  return {
    delayHours: r.delayHours,
    maxFollowups: r.maxFollowups,
    activeWindow: r.activeWindow,
    active: r.active,
  };
}

export function CampaignFollowupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const ruleQuery = useQuery({
    queryKey: ['admin', 'campaigns', id, 'followup-rule'],
    queryFn: () =>
      apiClient.get<{ rule: FollowupRuleDto | null }>(`/admin/campaigns/${id!}/followup-rule`),
    enabled: !!id,
  });

  const ruleExists = ruleQuery.data?.rule !== null && ruleQuery.data?.rule !== undefined;

  const messagesQuery = useQuery({
    queryKey: ['admin', 'campaigns', id, 'followup-messages'],
    queryFn: () =>
      apiClient.get<{ data: FollowupMessageDto[] }>(
        `/admin/campaigns/${id!}/followup-rule/messages`,
      ),
    // Only fetch messages when a rule exists — otherwise the endpoint 404s.
    enabled: !!id && ruleExists,
  });

  const messages = messagesQuery.data?.data ?? [];
  const poolIsEmpty = messages.length === 0;

  const upsertRuleMutation = useMutation({
    mutationFn: (v: FollowupRuleFormValue) =>
      apiClient.put<FollowupRuleDto>(`/admin/campaigns/${id!}/followup-rule`, {
        delayHours: v.delayHours,
        maxFollowups: v.maxFollowups,
        activeWindow: v.activeWindow,
        active: v.active,
      }),
    onSuccess: () => {
      toast.success('Regra de followup atualizada.');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'followup-rule'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'followup-messages'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao salvar regra.'));
    },
  });

  const createMessageMutation = useMutation({
    mutationFn: (content: string) =>
      apiClient.post<FollowupMessageDto>(`/admin/campaigns/${id!}/followup-rule/messages`, {
        content,
      }),
    onSuccess: () => {
      toast.success('Mensagem adicionada ao pool.');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'followup-messages'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao adicionar mensagem.'));
    },
  });

  const updateContentMutation = useMutation({
    mutationFn: ({ msgId, content }: { msgId: string; content: string }) =>
      apiClient.put<FollowupMessageDto>(`/admin/campaigns/${id!}/followup-rule/messages/${msgId}`, {
        content,
      }),
    onSuccess: () => {
      toast.success('Mensagem atualizada.');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'followup-messages'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao atualizar mensagem.'));
    },
  });

  const reorderMutation = useMutation({
    mutationFn: ({ msgId, position }: { msgId: string; position: number }) =>
      apiClient.put<FollowupMessageDto>(`/admin/campaigns/${id!}/followup-rule/messages/${msgId}`, {
        position,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'followup-messages'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao reordenar.'));
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: (msgId: string) =>
      apiClient.delete<{ deleted: boolean }>(
        `/admin/campaigns/${id!}/followup-rule/messages/${msgId}`,
      ),
    onSuccess: () => {
      toast.success('Mensagem removida.');
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'followup-messages'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao remover mensagem.'));
    },
  });

  if (!id) return null;

  if (ruleQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;
  }
  if (ruleQuery.error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft aria-hidden /> Voltar
        </Button>
        <div className="p-6 text-sm text-destructive">
          {humanizeError(ruleQuery.error, 'Falha ao carregar regra.')}
        </div>
      </div>
    );
  }

  const initialRule: FollowupRuleFormValue = ruleQuery.data?.rule
    ? toFormValue(ruleQuery.data.rule)
    : DEFAULT_FOLLOWUP_RULE;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/campaigns/${id}`)}>
            <ArrowLeft aria-hidden className="mr-1" /> Campanha
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">Followups</h1>
          <p className="text-sm text-muted-foreground">
            Configure o timing, a janela ativa e o pool de mensagens para esta campanha.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Regra</CardTitle>
        </CardHeader>
        <CardContent>
          <FollowupRuleForm
            initial={initialRule}
            poolIsEmpty={poolIsEmpty}
            submitting={upsertRuleMutation.isPending}
            onSubmit={(v) => upsertRuleMutation.mutate(v)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mensagens</CardTitle>
        </CardHeader>
        <CardContent>
          {!ruleExists ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Salve a regra acima antes de adicionar mensagens ao pool.
            </div>
          ) : messagesQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Carregando mensagens…</div>
          ) : messagesQuery.error ? (
            <div className="text-sm text-destructive">
              {humanizeError(messagesQuery.error, 'Falha ao carregar mensagens.')}
            </div>
          ) : (
            <FollowupMessagesList
              messages={messages.map((m) => ({
                id: m.id,
                ruleId: m.ruleId,
                position: m.position,
                content: m.content,
              }))}
              submitting={
                createMessageMutation.isPending ||
                updateContentMutation.isPending ||
                reorderMutation.isPending ||
                deleteMessageMutation.isPending
              }
              onCreate={(content) => createMessageMutation.mutate(content)}
              onUpdateContent={(msgId, content) => updateContentMutation.mutate({ msgId, content })}
              onDelete={(msgId) => deleteMessageMutation.mutate(msgId)}
              onReorder={(msgId, position) => reorderMutation.mutate({ msgId, position })}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CampaignFollowupPage;
